/**
 * Funzioni API per il portale job Prefect.
 * Chiamano il proxy product-service (GET /admin/prefect/*).
 */

import { createServiceClient } from '../../api/client.js';
import type { FlowRun, JobRun, LogLine } from './types.js';

/** Client parzialmente applicato per il product-service. */
const productApi = createServiceClient('product');

/**
 * Recupera gli ultimi flow-run Prefect.
 * GET /admin/prefect/flow-runs?limit=<n>
 */
export function fetchFlowRuns(limit = 50): Promise<FlowRun[]> {
  return productApi<FlowRun[]>(`/admin/prefect/flow-runs?limit=${limit}`);
}

/**
 * Vista /jobs: per ogni job l'ultima esecuzione (in corso o terminata) + placeholder
 * per i job mai eseguiti. I job in corso compaiono in cima.
 * GET /admin/prefect/job-runs
 */
export function fetchJobRuns(): Promise<JobRun[]> {
  return productApi<JobRun[]>(`/admin/prefect/job-runs`);
}

/**
 * Recupera le righe di log di un flow-run.
 * GET /admin/prefect/flow-runs/:id/logs?limit=<n>
 */
export function fetchFlowRunLogs(id: string, limit = 200): Promise<LogLine[]> {
  return productApi<LogLine[]>(
    `/admin/prefect/flow-runs/${encodeURIComponent(id)}/logs?limit=${limit}`,
  );
}

/**
 * Avvia un'esecuzione on-demand del deployment (senza modificarne lo schedule).
 * POST /admin/prefect/deployments/:id/run — richiede SYSTEM_CONFIGURATION lato server.
 */
export function runDeployment(deploymentId: string): Promise<{ id: string }> {
  return productApi<{ id: string }>(
    `/admin/prefect/deployments/${encodeURIComponent(deploymentId)}/run`,
    { method: 'POST' },
  );
}

/** Stato della scansione vulnerabilità manuale (pulsante header). */
export interface ManualScanStatus {
  /** false = deployment manual-vuln-scan assente in Prefect. */
  available: boolean;
  /** Run in corso o in coda, se c'è. */
  activeRun: FlowRun | null;
}

/**
 * Stato della scansione vulnerabilità forzata a mano.
 * GET /admin/prefect/manual-scan — VIEW_PORTFOLIO.
 */
export function fetchManualScanStatus(): Promise<ManualScanStatus> {
  return productApi<ManualScanStatus>(`/admin/prefect/manual-scan`);
}

/**
 * Avvia la scansione vulnerabilità forzata a mano (rianalisi DT → snapshot → triage → notifiche).
 * POST /admin/prefect/manual-scan — SYSTEM_CONFIGURATION; 409 se già in corso.
 */
export function startManualScan(): Promise<{ id: string }> {
  return productApi<{ id: string }>(`/admin/prefect/manual-scan`, { method: 'POST' });
}
